import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  BulkCreateTablesBody,
  BulkCreateTablesResult,
  CapacityModeBody,
  CapacityModeView,
  CreateShiftBody,
  CreateZoneBody,
  FloorPlan,
  ReservationShift,
  RestaurantTable,
  SetZoneTableLayoutBody,
  TableModeView,
  UpdateFloorPlanBody,
  UpdateRestaurantTableBody,
  UpdateShiftBody,
  UpdateZoneBody,
  Zone,
  ZoneTableLayoutResult,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const CAPACITY_MODE_PATH = '/api/restaurants/{restaurantId}/capacity-mode';
const TABLE_MODE_PATH = '/api/restaurants/{restaurantId}/table-mode';
const ZONE_TABLE_LAYOUT_PATH = '/api/restaurants/{restaurantId}/zones/{zoneId}/table-layout';
const ZONES_PATH = '/api/restaurants/{restaurantId}/zones';
const ZONE_PATH = '/api/restaurants/{restaurantId}/zones/{zoneId}';
const TABLES_PATH = '/api/restaurants/{restaurantId}/tables';
const TABLES_BULK_PATH = '/api/restaurants/{restaurantId}/tables/bulk';
const TABLE_PATH = '/api/restaurants/{restaurantId}/tables/{tableId}';
const SHIFTS_PATH = '/api/restaurants/{restaurantId}/shifts';
const SHIFT_PATH = '/api/restaurants/{restaurantId}/shifts/{shiftId}';
const FLOOR_PLAN_PATH = '/api/restaurants/{restaurantId}/floor-plan';

function useRestaurantId() {
  return useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
}

// ── Queries ────────────────────────────────────────────────────────────────

export function useCapacityMode() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'capacity-mode', restaurantId],
    queryFn: async () =>
      unwrapResult<CapacityModeView>(
        await openapiClient.GET(CAPACITY_MODE_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useTableMode() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'table-mode', restaurantId],
    queryFn: async () =>
      unwrapResult<TableModeView>(
        await openapiClient.GET(TABLE_MODE_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useZones() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'zones', restaurantId],
    queryFn: async () =>
      unwrapResult<Zone[]>(
        await openapiClient.GET(ZONES_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useTables(activeOnly?: boolean) {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'tables', restaurantId, activeOnly ?? false],
    queryFn: async () =>
      unwrapResult<RestaurantTable[]>(
        await openapiClient.GET(TABLES_PATH, {
          params: { path: { restaurantId }, query: { activeOnly } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function useShifts(activeOnly?: boolean) {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'shifts', restaurantId, activeOnly ?? false],
    queryFn: async () =>
      unwrapResult<ReservationShift[]>(
        await openapiClient.GET(SHIFTS_PATH, {
          params: { path: { restaurantId }, query: { activeOnly } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function useFloorPlan() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['setup', 'floor-plan', restaurantId],
    queryFn: async () =>
      unwrapResult<FloorPlan>(
        await openapiClient.GET(FLOOR_PLAN_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

function useInvalidate(key: string) {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['setup', key, restaurantId] });
}

export function useSetCapacityMode() {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CapacityModeBody) =>
      unwrapResult<CapacityModeView>(
        await openapiClient.PUT(CAPACITY_MODE_PATH, { params: { path: { restaurantId } }, body }),
      ),
    // Switching the mode changes which shifts are active and what's "set up".
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setup'] }),
  });
}

export function useSetTableMode() {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TableModeView) =>
      unwrapResult<TableModeView>(
        await openapiClient.PUT(TABLE_MODE_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setup'] }),
  });
}

export function useSetZoneTableLayout() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('tables');
  return useMutation({
    mutationFn: async ({ zoneId, body }: { zoneId: string; body: SetZoneTableLayoutBody }) =>
      unwrapResult<ZoneTableLayoutResult>(
        await openapiClient.PUT(ZONE_TABLE_LAYOUT_PATH, {
          params: { path: { restaurantId, zoneId } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

// Zones
export function useCreateZone() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('zones');
  return useMutation({
    mutationFn: async (body: CreateZoneBody) =>
      unwrapResult<Zone>(
        await openapiClient.POST(ZONES_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteZone() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('zones');
  return useMutation({
    mutationFn: async ({ zoneId }: { zoneId: string }) => {
      unwrapResult(
        await openapiClient.DELETE(ZONE_PATH, { params: { path: { restaurantId, zoneId } } }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateZone() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('zones');
  return useMutation({
    mutationFn: async ({ zoneId, body }: { zoneId: string; body: UpdateZoneBody }) =>
      unwrapResult<Zone>(
        await openapiClient.PATCH(ZONE_PATH, { params: { path: { restaurantId, zoneId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

// Tables
export function useUpdateTable() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('tables');
  return useMutation({
    mutationFn: async ({ tableId, body }: { tableId: string; body: UpdateRestaurantTableBody }) =>
      unwrapResult<RestaurantTable>(
        await openapiClient.PATCH(TABLE_PATH, {
          params: { path: { restaurantId, tableId } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

// Shifts
export function useCreateShift() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('shifts');
  return useMutation({
    mutationFn: async (body: CreateShiftBody) =>
      unwrapResult<ReservationShift>(
        await openapiClient.POST(SHIFTS_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateShift() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('shifts');
  return useMutation({
    mutationFn: async ({ shiftId, body }: { shiftId: string; body: UpdateShiftBody }) =>
      unwrapResult<ReservationShift>(
        await openapiClient.PATCH(SHIFT_PATH, {
          params: { path: { restaurantId, shiftId } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteShift() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('shifts');
  return useMutation({
    mutationFn: async ({ shiftId }: { shiftId: string }) => {
      unwrapResult(
        await openapiClient.DELETE(SHIFT_PATH, { params: { path: { restaurantId, shiftId } } }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteTable() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('tables');
  return useMutation({
    mutationFn: async (tableId: string) => {
      unwrapResult(
        await openapiClient.DELETE(TABLE_PATH, { params: { path: { restaurantId, tableId } } }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useBulkCreateTables() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('tables');
  return useMutation({
    mutationFn: async (body: BulkCreateTablesBody) =>
      unwrapResult<BulkCreateTablesResult>(
        await openapiClient.POST(TABLES_BULK_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

// Floor plan
export function useUpdateFloorPlan() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidate('floor-plan');
  return useMutation({
    mutationFn: async (body: UpdateFloorPlanBody) =>
      unwrapResult<FloorPlan>(
        await openapiClient.PUT(FLOOR_PLAN_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}
